import React, { useContext } from "react";
import {
    Grid,
    GridRow,
    GridColumn,
    Stack,
    ClearFiltersButton,
    ListTable,
    DropdownMultiselect,
    DatePicker,
    ButtonRenderProps,
    ContentSection
} from "sprint-common-web";

import { CloudAssetOrigin } from "../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../common/types/AssetStatus";
import { AssetType } from "../../../common/types/AssetType";
import { RecordType } from "../../../common/types/RecordType";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { mapEnumToDropdownOptions } from "../../../common/utils/mapEnumToDropdownOptions";
import { AssetsWarning } from "../common/AssetsWarning";
import {
    EditAssetListingColumnsConfigButton,
    EditAssetListingColumnsConfigButtonProps
} from "../common/config/EditFileRecordListingColumnsConfigButton";
import { createAssetListTableColumns } from "../common/createAssetListTableColumns";
import { renderAssetRow } from "../common/renderAssetRow";
import { AncillaryAssetsListBreadcrumbs } from "./AncillaryAssetsListBreadcrumbs";
import { SingleAncillaryContext } from "./context/SingleAncillaryContext";
import { useAncillaryAssets } from "./hooks/useAncillaryAssets";
import { useAncillaryAssetsListFilters } from "./hooks/useAncillaryAssetsListFilters";
import { ReadOnlyMultiselectSearchDropdown } from "./utils/ReadOnlyMultiselectSearchDropdown";

export const AncillaryAssetsList: React.FC = () => {
    const { ancillary, isLoading: isContextLoading, listingConfig, saveListingConfig } = useContext(
        SingleAncillaryContext
    );

    const {
        filters,
        versionDetails,
        setStatuses,
        setPage,
        setPageSize,
        setSort,
        clearFilters,
        setAssetTypes,
        setAssetOrigins,
        setAssetRecordTypes,
        setCreatedDateFrom,
        setCreatedDateTo,
        setUpdatedDateFrom,
        setUpdatedDateTo
    } = useAncillaryAssetsListFilters();

    const { items, totalItems, isLoading } = useAncillaryAssets({ filters });

    const columns = createAssetListTableColumns({ config: listingConfig });

    const configButton: ButtonRenderProps<EditAssetListingColumnsConfigButtonProps> = {
        render: EditAssetListingColumnsConfigButton,
        listingConfig,
        saveListingConfig,
        pageSize: filters.pageSize,
        setPageSize: setPageSize
    };

    return (
        <>
            <PageHeader>
                <AncillaryAssetsListBreadcrumbs
                    ancillaryTitle={ancillary.title}
                    ancillaryVersionType={ancillary.version.type.name}
                    ancillaryAirlines={ancillary.version.airlines}
                    contentType={ancillary.contentType}
                    isLoading={isContextLoading}
                />
            </PageHeader>
            <AssetsWarning items={items} />
            <ContentSection header="Ancillary assets" buttons={[configButton]}>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <ReadOnlyMultiselectSearchDropdown
                                values={versionDetails.distributors}
                                placeholder="Distributors"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <ReadOnlyMultiselectSearchDropdown
                                values={versionDetails.versionTypes}
                                placeholder="Version types"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <ReadOnlyMultiselectSearchDropdown
                                values={versionDetails.airlines}
                                placeholder="Airlines"
                            />
                        </GridColumn>

                        <GridColumn sm={6}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton isLabeled={true} onClear={clearFilters} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Statuses"
                                options={mapEnumToDropdownOptions(AssetStatus)}
                                selectedKeys={filters.statuses?.map((status) => status.toString())}
                                onChange={setStatuses}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset type"
                                selectedKeys={filters.types?.map((t) => t.toString())}
                                onChange={setAssetTypes}
                                options={mapEnumToDropdownOptions(AssetType)}
                                testId="ancillary-asset-types-filter"
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset record type"
                                selectedKeys={filters.recordTypes?.map((t) => t.toString())}
                                onChange={setAssetRecordTypes}
                                options={mapEnumToDropdownOptions(RecordType)}
                                testId="movie-asset-record-types-filter"
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset origin"
                                selectedKeys={filters.origins?.map((t) => t.toString())}
                                onChange={setAssetOrigins}
                                options={mapEnumToDropdownOptions(CloudAssetOrigin)}
                                testId="movie-asset-origins-filter"
                            />
                        </GridColumn>
                    </GridRow>

                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-start-date-from"
                                placeholder="Created date from"
                                value={filters.createdDateFrom}
                                onDateSelect={setCreatedDateFrom}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-start-date-to"
                                placeholder="Created date to"
                                value={filters.createdDateTo}
                                onDateSelect={setCreatedDateTo}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-due-date-from"
                                placeholder="Updated date from"
                                value={filters.updatedDateFrom}
                                onDateSelect={setUpdatedDateFrom}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="assets-filters-due-date-to"
                                placeholder="Updated date to"
                                value={filters.updatedDateTo}
                                onDateSelect={setUpdatedDateTo}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn>
                            <ListTable
                                columns={columns}
                                items={items}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                onRenderRow={renderAssetRow}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isContextLoading || isLoading}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};
